import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Container, Typography, Paper, Button, TextField, Stack, CircularProgress } from '@mui/material';
import { Helmet }         from 'react-helmet-async';
import EditIcon           from '@mui/icons-material/Edit';
import axiosPublic        from '../api/axiosPublic';
import axiosInstance      from '../api/axiosConfig';
import { useAuth }        from '../context/AuthContext';

const WALNUT     = '#2C1F14';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const TERRACOTTA = '#C4622D';

export default function PrivacyPolicy() {
    const { user } = useAuth();
    const canEdit  = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [content,  setContent]  = useState('');
    const [loading,  setLoading]  = useState(true);
    const [editing,  setEditing]  = useState(false);
    const [draft,    setDraft]    = useState('');
    const [saving,   setSaving]   = useState(false);

    useEffect(() => {
        axiosPublic.get('/api/info')
            .then(res => setContent(res.data?.privacy || ''))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axiosInstance.put('/api/info', { privacy: draft });
            setContent(res.data?.privacy || '');
            setEditing(false);
        } catch {}
        finally { setSaving(false); }
    };

    return (
        <Box sx={{ bgcolor: PARCHMENT, minHeight: '100vh' }}>
            <Helmet><title>Privacy Policy — HayTour</title></Helmet>

            <Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 } }}>
                <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.22em', fontWeight: 700 }}>
                    Legal
                </Typography>
                <Typography variant="h4" fontWeight={800}
                    sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", mt: 0.5 }}>
                    Privacy Policy
                </Typography>
            </Box>

            <Container maxWidth="md" sx={{ py: 6 }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid rgba(196,98,45,0.12)' }}>
                    {canEdit && !editing && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button startIcon={<EditIcon />} size="small" variant="outlined"
                                onClick={() => { setDraft(content); setEditing(true); }}
                                sx={{ color: TERRACOTTA, borderColor: 'rgba(196,98,45,0.4)', borderRadius: 2 }}>
                                Edit
                            </Button>
                        </Box>
                    )}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} sx={{ color: TERRACOTTA }} />
                        </Box>
                    ) : editing ? (
                        <Stack spacing={2}>
                            <TextField multiline fullWidth minRows={6} value={draft}
                                onChange={e => setDraft(e.target.value)} autoFocus />
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                                <Button variant="contained" onClick={handleSave} disabled={saving}
                                    sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2 }}>
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <>
                            {content ? (
                                <ReactMarkdown components={{
                                    p:  (props) => <Typography variant="body1" paragraph sx={{ color: WALNUT, lineHeight: 1.8 }} {...props} />,
                                    h1: (props) => <Typography variant="h5" fontWeight={700} sx={{ color: WALNUT, mt: 2, mb: 1 }} {...props} />,
                                    h2: (props) => <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mt: 2, mb: 1 }} {...props} />,
                                    h3: (props) => <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mt: 1.5, mb: 0.5 }} {...props} />,
                                }}>{content}</ReactMarkdown>
                            ) : (
                                <Typography variant="body1" color="text.secondary">Content coming soon.</Typography>
                            )}
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    );
}
